import React, { forwardRef } from 'react';
import { Editor as WysiwygEditor } from 'react-draft-wysiwyg';
import { ContentState, convertToRaw, EditorState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { MergeFieldToolbarOption } from '../MergeFieldToolbarOption';

const convertValue = (value) => {
  if (!value) {
    return null;
  }
  const blocksFromHtml = htmlToDraft(value);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  return EditorState.createWithContent(contentState);
};

export type WysiwygProps = {
  defaultValue?: string | undefined;
  value: string;
  onChange?: (wysiwygHtmlContent) => void;
  onBlur?: (wysiwygHtmlContent) => void;
  allowImages?: boolean;
  allowColorPicker?: boolean;
  allowEmoji?: boolean;
  hideToolbar?: boolean;
  toolbarOptions?: Array<string>;
};

const Wysiwyg = forwardRef<any, WysiwygProps>(
  (
    {
      defaultValue,
      value,
      onChange,
      onBlur,
      allowImages = false,
      allowColorPicker = false,
      allowEmoji = false,
      hideToolbar = false,
      toolbarOptions = ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'link'],
    },
    ref
  ) => {
    const defaultEditorState = convertValue(defaultValue || value) || EditorState.createEmpty();

    if (allowImages) {
      toolbarOptions.push('image');
    }

    if (allowEmoji) {
      toolbarOptions.push('emoji');
    }

    if (allowColorPicker) {
      toolbarOptions.push('colorPicker');
    }

    const onChangeWrapper = (rawContent) => {
      if (onChange) {
        onChange(draftToHtml(rawContent));
      }
    };

    const onBlurWrapper = (event, editorState) => {
      if (onBlur) {
        onBlur(draftToHtml(convertToRaw(editorState.getCurrentContent())));
      }
    };

    //Note - Key is important, forces a new component to mount when default value changes, otherwise it will not reset to the default.
    return (
      <WysiwygEditor
        ref={ref}
        key={`${defaultValue}`}
        defaultEditorState={defaultEditorState}
        onChange={onChangeWrapper}
        onBlur={onBlurWrapper}
        wrapperClassName="rdw-wrapper"
        editorClassName="rdw-editor"
        toolbarHidden={hideToolbar}
        toolbar={{
          options: toolbarOptions,
          image: {
            className: undefined,
            component: undefined,
            popupClassName: undefined,
            urlEnabled: true,
            uploadEnabled: true,
            alignmentEnabled: true,
            uploadCallback: undefined,
            previewImage: true,
            inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
            alt: { present: true, mandatory: false },
            defaultSize: {
              height: 'auto',
              width: 'auto',
            },
          },
        }}
        toolbarCustomButtons={[<MergeFieldToolbarOption />]}
      />
    );
  }
);

export default Wysiwyg;
